import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

/**
 * Adaptadores de compatibilidad react-router v5 → v7.
 *
 * DEUDA TÉCNICA. Estos adaptadores existen sólo para no reescribir a mano los
 * ~250 archivos que consumían la API de v5 (`withRouter`, `useHistory`,
 * `useRouteMatch`, `<Route render>`). Reproducen exactamente la parte de la API
 * de v5 que el código usa, y nada más:
 *
 *   - de `history` sólo se usan `push` y `replace` (118 y 6 llamadas);
 *   - de `match` sólo se usan `params` y `url` (82 y 13 lecturas).
 *
 * La forma de saldarla es archivo por archivo: reemplazar `useHistory()` por
 * `useNavigate()`, `withRouter` por los hooks nativos en los componentes de
 * clase que se vayan convirtiendo, y `RenderRoute` por `element={<X />}`.
 * Cuando `src/utils/router.js` deje de tener consumidores, se borra.
 *
 * Lo que NO reproducen (verificado: nadie lo usa):
 *   - `history.listen` / `history.block` / `history.length`;
 *   - `match.isExact` real — aquí siempre es `true`;
 *   - `match.path` como patrón — aquí es el pathname ya resuelto;
 *   - el izado de estáticos que hacía `withRouter` de v5.
 */

const sinBarraFinal = (pathname) =>
	pathname.length > 1 && pathname.endsWith("/")
		? pathname.slice(0, -1)
		: pathname;

/**
 * `useHistory` de v5 sobre `useNavigate` de v7.
 * `push`/`replace` aceptan string o un objeto tipo location, igual que v5.
 */
export function useHistory() {
	const navigate = useNavigate();
	const location = useLocation();

	return React.useMemo(
		() => ({
			push: (to, state) => navigate(to, { state }),
			replace: (to, state) => navigate(to, { state, replace: true }),
			go: (delta) => navigate(delta),
			goBack: () => navigate(-1),
			goForward: () => navigate(1),
			location,
		}),
		[navigate, location]
	);
}

/**
 * `useRouteMatch` de v5. Todos los usos del repo leen `params` o `url`, y todos
 * ocurren en componentes montados en su ruta exacta, así que `url` es el
 * pathname actual sin la barra final.
 */
export function useRouteMatch() {
	const params = useParams();
	const { pathname } = useLocation();
	const url = sinBarraFinal(pathname);

	return { params, url, path: url, isExact: true };
}

/** Las tres props que `<Route component>` y `<Route render>` inyectaban en v5. */
function useRouterProps() {
	return {
		history: useHistory(),
		location: useLocation(),
		match: useRouteMatch(),
	};
}

/**
 * `withRouter` de v5: inyecta `history`, `location` y `match` como props.
 * Sigue siendo la vía de acceso al router para los componentes de clase.
 */
export function withRouter(Component) {
	function ComponenteConRouter(props) {
		return <Component {...props} {...useRouterProps()} />;
	}

	ComponenteConRouter.displayName = `withRouter(${
		Component.displayName || Component.name || "Component"
	})`;

	return ComponenteConRouter;
}

/**
 * Puente para `<Route render={fn} />`, que v7 no acepta:
 *
 *   <Route path=':id' render={(props) => <Edit {...props} />} />
 *   <Route path=':id' element={<RenderRoute render={(props) => <Edit {...props} />} />} />
 *
 * Llama a la función con las mismas props que le pasaba v5, así que el cuerpo
 * de la función no cambia.
 */
export function RenderRoute({ render }) {
	return render(useRouterProps());
}
